import React, { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import QuestionSection from './QuestionSection';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

function InterviewSession() {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const test_id = searchParams.get("test_id");
  
  // State variables
  const [hasEnded, setHasEnded] = useState(false);
  const [testInProgress, setTestInProgress] = useState(false);
  const [sessionData, setSessionData] = useState(null);
  const [showRefreshWarning, setShowRefreshWarning] = useState(false);
  
  // Refs for tracking
  const sessionIdRef = useRef(Date.now().toString(36) + Math.random().toString(36).substr(2));

  // Check on initial load if we're returning from a refresh
  useEffect(() => {
    const savedSession = sessionStorage.getItem(`interviewSession_${test_id}`);
    if (savedSession) {
      try {
        const session = JSON.parse(savedSession);
        setSessionData(session);
        
        // Check if the test was already ended
        if (session.hasEnded) {
          setHasEnded(true);
          sessionStorage.removeItem(`interviewSession_${test_id}`);
        } else {
          // Only show refresh warning if test was in progress and not ended
          setShowRefreshWarning(true);
          
          // If interview was in progress before refresh
          if (session.testInProgress) {
            setTestInProgress(true);
          }
        }
      } catch (e) {
        console.error('Failed to parse saved session data');
        sessionStorage.removeItem(`interviewSession_${test_id}`);
      }
    } else {
      // This is a new session, save initial state to sessionStorage
      Object.keys(sessionStorage).forEach((key) => {
        if (key.startsWith('interviewSession_') && key !== `interviewSession_${test_id}`) {
          sessionStorage.removeItem(key);
        }
      });

      const newSession = {
        sessionId: sessionIdRef.current,
        startTime: new Date().toISOString(),
        testId: test_id,
        testInProgress: false,
        hasEnded: false
      };
      sessionStorage.setItem(`interviewSession_${test_id}`, JSON.stringify(newSession));
    }
    
    return () => {
      // Clean up by removing session data when component unmounts normally
      if (!hasEnded) {
        sessionStorage.removeItem(`interviewSession_${test_id}`);
      }
    };
  }, []);

  useEffect(() => {
    if (hasEnded) {
      // This will run immediately when hasEnded becomes true
      sessionStorage.removeItem(`interviewSession_${test_id}`);
    }
  }, [hasEnded]);

  // Save session data whenever important state changes
  useEffect(() => {
    // Don't save session if the interview has ended
    if (!hasEnded) {
      const currentSession = {
        sessionId: sessionIdRef.current,
        startTime: sessionData?.startTime || new Date().toISOString(),
        testId: test_id,
        testInProgress: testInProgress,
        hasEnded: false
      };
      sessionStorage.setItem(`interviewSession_${test_id}`, JSON.stringify(currentSession));
    }
  }, [testInProgress, hasEnded]);
  
  // Handle interview submission
  const handleSubmitInterview = async () => {
    if (hasEnded) return;
    setHasEnded(true);
    
    // Clear session data
    try {
      sessionStorage.removeItem(`interviewSession_${test_id}`);
      
      // Send data to server
      navigator.sendBeacon(`/api/save_score_in_bg?test_id=${test_id}`, { method: "POST" });
      
      // Add a small delay before redirecting to ensure session is cleared
      setTimeout(() => {
        window.location.href = "/thankyou";
      }, 100);
    } catch (err) {
      console.error("Error in submit interview:", err);
      // Even if there's an error, try to navigate away
      window.location.href = "/thankyou";
    }
  };
  
  // Handle resume after refresh warning
  const handleResumeInterview = () => {
    setShowRefreshWarning(false);
    setTestInProgress(true);
  };

  const handleReturnHome = () => {
    window.location.href = "/user-home";
  };

  return (
    <>
      {/* Refresh warning */}
      <Dialog open={showRefreshWarning} onOpenChange={() => {}}>
        <DialogContent className={cn("sm:max-w-[425px]")}>
          <DialogHeader>
            <DialogTitle>🔄 Interview Session Detected</DialogTitle>
            <DialogDescription>
              It seems you refreshed the page or temporarily left the interview.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className={cn("flex flex-col sm:flex-row gap-3")}>
            <Button onClick={handleResumeInterview} className={cn("w-full sm:w-auto")}>Resume Interview</Button>
            <Button variant="outline" onClick={handleSubmitInterview} className={cn("w-full sm:w-auto")}>Submit Interview Now</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Main Interview Content - QuestionSection is now full-screen */}
      {(!hasEnded && !showRefreshWarning) && <QuestionSection />}
      
      {/* Completion message when interview ends */}
      <Dialog open={hasEnded} onOpenChange={() => {}}>
        <DialogContent className={cn("sm:max-w-[425px] text-center")}>
          <DialogHeader>
            <DialogTitle className={cn("text-2xl font-bold")}>Interview Submitted</DialogTitle>
            <DialogDescription>
              Your interview has been successfully submitted. Thank you!
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className={cn("flex justify-center")}>
            <Button onClick={handleReturnHome} className={cn("w-full sm:w-auto")}>
              Return to Home
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default InterviewSession;